import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

import StarfieldCanvas from './components/StarfieldCanvas';
import Sidebar from './components/Sidebar';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import WhatsApp from './pages/WhatsApp';
import Leads from './pages/Leads';
import Users from './pages/Users';
import Broadcast from './pages/Broadcast';
import BroadcastAnalytics from './pages/BroadcastAnalytics';
import Templates from './pages/Templates';

// ─── Org Layout ───────────────────────────────────────────────────────────────
function OrgLayout() {
  const { user } = useAuth();
  const features = user?.allowedFeatures || [];
  const isSuperAdmin = user?.role === 'superadmin';

  function featureRoute(feature, path, element) {
    if (!isSuperAdmin && !features.includes(feature)) return null;
    return <Route path={path} element={element} />;
  }

  return (
    <SocketProvider>
      <div style={layoutStyle}>
        <Sidebar />
        <main style={mainStyle}>
          <Routes>
            {featureRoute('dashboard',  '/',                      <Dashboard />)}
            {featureRoute('whatsapp',   '/whatsapp',              <WhatsApp />)}
            {featureRoute('broadcast',  '/broadcast',             <Broadcast />)}
            {featureRoute('broadcast',  '/broadcast/analytics',   <BroadcastAnalytics />)}
            {featureRoute('templates',  '/templates',             <Templates />)}
            {featureRoute('leads',      '/leads',                 <Leads />)}
            {featureRoute('users',      '/users',                 <Users />)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </SocketProvider>
  );
}

// ─── Private Route Gate ───────────────────────────────────────────────────────
function PrivateLayout() {
  const { user, logout, loading } = useAuth();

  if (loading) return (
    <div style={{ color: '#64748b', background: '#010614', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#818cf8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 14 }}>Loading...</span>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;

  return <OrgLayout />;
}

// ─── SSO Handler ─────────────────────────────────────────────────────────────
function SSOHandler() {
  const { ssoLogin, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (loading || attempted.current) return;
    if (user) { navigate('/', { replace: true }); return; }
    const token = new URLSearchParams(location.search).get('token');
    if (!token) { navigate('/login', { replace: true }); return; }
    attempted.current = true;
    ssoLogin(token)
      .then(() => navigate('/', { replace: true }))
      .catch(err => setError(err.response?.data?.error || 'SSO login failed. Please sign in manually.'));
  }, [loading, user, ssoLogin, navigate, location.search]);

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#010614', color: '#f87171', gap: 16 }}>
      <p style={{ fontSize: 15 }}>{error}</p>
      <button onClick={() => navigate('/login', { replace: true })} style={{ padding: '10px 24px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14 }}>
        Sign in manually
      </button>
    </div>
  );
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#010614' }}>
      <div style={{ width: 36, height: 36, border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#818cf8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Public Route Guard ───────────────────────────────────────────────────────
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <StarfieldCanvas />
        <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/sso"   element={<SSOHandler />} />
            <Route path="/*"     element={<PrivateLayout />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

const layoutStyle = { display: 'flex', minHeight: '100vh', background: 'transparent' };
const mainStyle   = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'transparent' };
